import type { Block } from '@/components/canvas/blocks/types';
import { selectionChromeWorldMetrics } from '@/components/canvas/selection/selectionChrome';
import { RectHandles } from '@/components/canvas/selection/handles/RectHandles';
import type { ResizeHandle } from '@/components/canvas/transform';

function blockRect(
  block: Block,
  dragOffset: { dx: number; dy: number },
  resizePreview: { x: number; y: number; width: number; height: number } | null,
  resizeBlockId: string | null,
) {
  if (resizeBlockId === block.id && resizePreview) {
    return resizePreview;
  }
  return {
    x: block.layout.x + dragOffset.dx,
    y: block.layout.y + dragOffset.dy,
    width: block.layout.width,
    height: block.layout.height,
  };
}

export interface BlockChromeLayerProps {
  blocks: Block[];
  selectedBlockIds: string[];
  blockDragOffset: { dx: number; dy: number };
  resizePreview: { x: number; y: number; width: number; height: number } | null;
  resizeState: { id: string } | null;
  zoom: number;
  hideChrome: boolean;
  onStartBlockResize: (block: Block, handle: ResizeHandle, e: React.MouseEvent) => void;
}

/** 功能块缩放手柄（与元素 HandleLayer 一致：仅手柄可点，不挡块体点击） */
export function BlockChromeLayer({
  blocks,
  selectedBlockIds,
  blockDragOffset,
  resizePreview,
  resizeState,
  zoom,
  hideChrome,
  onStartBlockResize,
}: BlockChromeLayerProps) {
  if (hideChrome || selectedBlockIds.length !== 1) return null;

  const block = blocks.find((b) => b.id === selectedBlockIds[0]);
  if (!block) return null;

  const rect = blockRect(block, blockDragOffset, resizePreview, resizeState?.id ?? null);
  const { inset, pad } = selectionChromeWorldMetrics(zoom);
  const w = rect.width + pad;
  const h = rect.height + pad;

  return (
    <div className="pointer-events-none absolute inset-0 z-[7]" aria-hidden>
      <div
        className="absolute pointer-events-none"
        style={{
          left: rect.x - inset,
          top: rect.y - inset,
          width: w,
          height: h,
        }}
      >
        <RectHandles
          element={
            {
              id: block.id,
              type: 'rect',
              layout: { x: 0, y: 0, zIndex: 0 },
              size: { width: rect.width, height: rect.height },
            } as never
          }
          rect={{ x: inset, y: inset, width: rect.width, height: rect.height }}
          zoom={zoom}
          isLine={false}
          lineEndX={0}
          lineEndY={0}
          currentRadius={0}
          showRotate={false}
          showRadiusHandles={false}
          onStartResize={(handle, e) => onStartBlockResize(block, handle, e)}
          onStartLineHandle={() => {}}
          onStartRotate={() => {}}
          onStartRadiusDrag={() => {}}
        />
      </div>
    </div>
  );
}
